import { z } from 'zod'
import { moneySchema, optionalText, versionSchema } from './common.js'
import type { ProjectUserRef } from './projects.js'
import type { CostCategoryRef, ProjectFactParty, WbsNodeRef } from './projects-costs.js'
import type { InvoiceStatus, PaymentStatus } from './sales.js'

/**
 * Projects release P3: billing through Sales (docs/refactor/projects/03-billing-and-time.md §2–8,
 * P3_BREAKDOWN.md). Every document Projects makes is a posted Sales invoice (or credit note) created by
 * `ProjectBillingService.issue`, idempotent per `requestId`, linked in `project_billing_documents` and
 * `project_billed_lines`, and voided only from the project. Amounts are ex-VAT; Projects invoices use
 * `pricesIncludeTax: false`. Every amount here needs `projects.financials`.
 */

const nullableUuid = z
  .uuid()
  .nullish()
  .transform((v) => v ?? null)
const nullableDate = z.iso
  .date()
  .nullish()
  .transform((v) => v ?? null)
const requiredText = (max: number, message = 'Required') => z.string().trim().min(1, message).max(max)

// ── kinds ────────────────────────────────────────────────────────────────────

/**
 * What a Projects document bills. MILESTONE: billing plan items. TM: billable costs (and time, P6).
 * CLAIM, ADVANCE, RETENTION_RELEASE, ADVANCE_REDUCTION: contracting (P4–P5).
 */
export const PROJECT_BILLING_KINDS = [
  'MILESTONE',
  'TM',
  'CLAIM',
  'ADVANCE',
  'RETENTION_RELEASE',
  'ADVANCE_REDUCTION',
] as const
export type ProjectBillingKind = (typeof PROJECT_BILLING_KINDS)[number]
/** The kinds issued in this release. */
export const PROJECT_BILLING_KINDS_AVAILABLE: readonly ProjectBillingKind[] = ['MILESTONE', 'TM']

// ── billing plan ─────────────────────────────────────────────────────────────

/** FIXED: `amount`. PERCENT: `percent` of the original contract value, worked out when invoiced (ASM-197). */
export const BILLING_ITEM_BASES = ['FIXED', 'PERCENT'] as const
export type BillingItemBasis = (typeof BILLING_ITEM_BASES)[number]

/** DATE: due on `plannedDate`. MILESTONE: when the plan milestone is achieved. MANUAL: "Mark ready". */
export const BILLING_ITEM_TRIGGERS = ['DATE', 'MILESTONE', 'MANUAL'] as const
export type BillingItemTrigger = (typeof BILLING_ITEM_TRIGGERS)[number]

/**
 * Derived, never stored. INVOICED: an active billed line of an ACTIVE document points at it. READY:
 * not invoiced and its trigger has fired (DATE on or before today in Muscat, MILESTONE achieved, MANUAL
 * marked). PLANNED otherwise. A cancelled item is never billed and is left out of totals.
 */
export const BILLING_ITEM_STATUSES = ['PLANNED', 'READY', 'INVOICED', 'CANCELLED'] as const
export type BillingItemStatus = (typeof BILLING_ITEM_STATUSES)[number]

const billingItemFields = z.object({
  /** The invoice line's text. */
  description: requiredText(500),
  /** With trigger MILESTONE it must be a MILESTONE node of the project; otherwise optional. */
  wbsNodeId: nullableUuid,
  basis: z.enum(BILLING_ITEM_BASES),
  /** FIXED only: above zero. */
  amount: moneySchema.nullish().transform((v) => v ?? null),
  /** PERCENT only: above 0 and at most 100, two decimals. */
  percent: z
    .string()
    .regex(/^\d{1,3}(\.\d{1,2})?$/, 'Invalid percentage')
    .nullish()
    .transform((v) => v ?? null),
  /** null: the project's tax rate. */
  taxRateId: nullableUuid,
  trigger: z.enum(BILLING_ITEM_TRIGGERS),
  /** DATE: required. Others: the expected date, optional. */
  plannedDate: nullableDate,
})

type BillingItemFields = z.output<typeof billingItemFields>
/** Cross-field rules of a billing item (also applied by the API). */
export function billingItemErrors(v: BillingItemFields): { path: string; message: string }[] {
  const errors: { path: string; message: string }[] = []
  if (v.basis === 'FIXED') {
    if (v.amount === null || !(Number(v.amount) > 0))
      errors.push({ path: 'amount', message: 'Enter an amount above zero' })
    if (v.percent !== null) errors.push({ path: 'percent', message: 'A fixed item has no percentage' })
  } else {
    if (v.percent === null || !(Number(v.percent) > 0) || Number(v.percent) > 100) {
      errors.push({ path: 'percent', message: 'Enter a percentage above 0 and at most 100' })
    }
    if (v.amount !== null) errors.push({ path: 'amount', message: 'A percentage item has no amount' })
  }
  if (v.trigger === 'DATE' && v.plannedDate === null) {
    errors.push({ path: 'plannedDate', message: 'Choose the date it is billed' })
  }
  if (v.trigger === 'MILESTONE' && v.wbsNodeId === null) {
    errors.push({ path: 'wbsNodeId', message: 'Choose the milestone that triggers it' })
  }
  return errors
}
const refineItem = (v: BillingItemFields, ctx: z.RefinementCtx) => {
  for (const e of billingItemErrors(v)) ctx.addIssue({ code: 'custom', path: [e.path], message: e.message })
}

/** `POST /projects/:id/billing-items`: added last (manage* and projects.financials; project open). */
export const billingItemInputSchema = billingItemFields.superRefine(refineItem)
export type BillingItemInput = z.input<typeof billingItemInputSchema>
/** `PATCH /projects/:id/billing-items/:itemId`: an INVOICED or cancelled item can't change (409 IN_USE). */
export const billingItemUpdateSchema = billingItemFields
  .extend({ version: versionSchema })
  .superRefine(refineItem)
export type BillingItemUpdate = z.input<typeof billingItemUpdateSchema>
/** `POST …/:itemId/ready` (MANUAL items) and `POST …/:itemId/unready`. */
export const billingItemCommandSchema = z.object({ version: versionSchema })
/** `POST …/:itemId/cancel`: a reason; never-billed items may instead be deleted. */
export const billingItemCancelSchema = z.object({
  version: versionSchema,
  reason: requiredText(500, 'Give a reason'),
})
/** `PUT /projects/:id/billing-items/order`: the full order of the plan's items. */
export const billingItemOrderSchema = z.object({ itemIds: z.array(z.uuid()).max(500) })

export interface TaxRateRefLite {
  id: string
  name: string
  nameAr: string | null
  /** Percentage, e.g. "5.00". */
  rate: string
}

export interface BillingDocumentRef {
  /** The `project_billing_documents` row. */
  documentId: string
  kind: ProjectBillingKind
  invoiceId: string | null
  creditNoteId: string | null
  number: string
  date: string
  status: 'ACTIVE' | 'VOID'
  total: string
  /** The invoice's open balance (Sales' figure); null for credit notes. */
  balance: string | null
  paymentStatus: PaymentStatus | null
  openable: boolean
}

export interface BillingItem {
  id: string
  position: number
  description: string
  wbsNode: WbsNodeRef | null
  basis: BillingItemBasis
  amount: string | null
  percent: string | null
  /**
   * What it bills: once INVOICED, the amount its active billed line carries; otherwise the amount, or the
   * percentage of the current original contract value.
   */
  billAmount: string
  taxRate: TaxRateRefLite | null
  trigger: BillingItemTrigger
  plannedDate: string | null
  readyAt: string | null
  status: BillingItemStatus
  cancelReason: string | null
  cancelledAt: string | null
  cancelledBy: ProjectUserRef | null
  /** The active document that billed it. */
  invoice: BillingDocumentRef | null
  can: { edit: boolean; delete: boolean; cancel: boolean; ready: boolean; unready: boolean; invoice: boolean }
  version: number
}

export interface BillingPlan {
  items: BillingItem[]
  /** Σ bill amounts of items not cancelled. */
  planTotal: string
  /** planTotal ÷ original contract value × 100, two decimals; null without a contract value. */
  coversPct: string | null
  contractValue: string | null
}

// ── documents and figures ────────────────────────────────────────────────────

export const PROJECT_BILLING_DOCUMENT_STATUSES = ['ACTIVE', 'VOID'] as const

/**
 * `GET /projects/:id/billing-documents`: every POSTED and VOID invoice, debit note and credit note
 * with the project's tag (03 §8), Projects-issued or tagged in Sales, newest first.
 */
export interface ProjectSalesDocument {
  /** Set when Projects issued it. */
  billingDocumentId: string | null
  kind: ProjectBillingKind | null
  documentType: 'INVOICE' | 'DEBIT_NOTE' | 'CREDIT_NOTE'
  id: string
  number: string | null
  date: string
  status: InvoiceStatus
  subtotal: string
  total: string
  /** Invoices and debit notes: Sales' open balance; credit notes: null. */
  balance: string | null
  paymentStatus: PaymentStatus | null
  voidReason: string | null
  openable: boolean
  /** Projects-issued and ACTIVE, and the viewer holds projects.bill. */
  canVoid: boolean
  /**
   * Why Sales would refuse the void now, so the button is disabled with its reason: receipts or credit
   * notes applied to it, or its period is locked. Null when nothing stands in the way.
   */
  voidBlocked: 'HAS_PAYMENTS' | 'HAS_CREDIT_NOTES' | 'PERIOD_LOCKED' | null
}

/**
 * `GET /projects/:id/billing/summary` (03 §8). Billed and collected come from the REVENUE and
 * COLLECTION facts; invoiced (gross) and outstanding reuse Sales' own figures, so the tab always
 * matches Sales.
 */
export interface ProjectBillingSummary {
  asOf: string
  contractValue: string | null
  /** Ex-VAT: invoices and debit notes − credit notes (plus legacy opening billed). */
  billed: string
  /** VAT-inclusive totals of POSTED invoices and debit notes − POSTED credit notes. */
  invoiced: string
  /** Receipt allocations to the project's invoices (plus legacy opening collected). */
  collected: string
  /** Σ open balances of the project's POSTED invoices. */
  outstanding: string
  /** collected ÷ invoiced × 100, two decimals; null when nothing is invoiced. */
  collectionPct: string | null
  /** READY billing items. */
  readyToBill: string
  /** Billable T&M amounts not yet invoiced (after overrides). */
  unbilledTm: string
  /** Legacy figures before go-live (03 §9), shown as their own line. */
  opening: { asOf: string; billed: string; collected: string } | null
  /** The company has Sales off: billing waits (actions hidden). */
  salesOff: boolean
}

// ── time-and-materials (costs in P3; time in P6) ─────────────────────────────

/** The sources a T&M invoice bills (03 §6.1). Time entries join in P6. */
export const TM_SOURCE_TYPES = [
  'purchase',
  'supplier_return',
  'expense',
  'project_material_issue',
  'project_cost_entry',
  'project_time_entry',
] as const
export type TmSourceType = (typeof TM_SOURCE_TYPES)[number]

/** One billable source line: `sourceType:sourceId:sourceLineId` (line id empty for expenses). */
export const tmSourceKeySchema = z
  .string()
  .regex(/^[a-z_]+:[0-9a-f-]{36}:([0-9a-f-]{36})?$/, 'Invalid source')
export function tmSourceKey(s: {
  sourceType: string
  sourceId: string
  sourceLineId: string | null
}): string {
  return `${s.sourceType}:${s.sourceId}:${s.sourceLineId ?? ''}`
}

/**
 * How a T&M invoice groups its lines. Costs: one line per cost category, or per source line with ENTRY.
 * Time (P6): ROLE one line per rate role and rate, PERSON per person and rate, ENTRY per entry.
 */
export const TM_GROUPINGS = ['ROLE', 'PERSON', 'ENTRY'] as const
export type TmGrouping = (typeof TM_GROUPINGS)[number]

/** `GET /projects/:id/unbilled?through=`: billable sources not in an active billed line, oldest first. */
export const unbilledQuerySchema = z.object({
  /** Only sources dated on or before this date. */
  through: z.iso.date().optional(),
  /** Include lines written off (not billable) so they can be put back. */
  includeWrittenOff: z.stringbool().optional(),
})
export type UnbilledQuery = z.input<typeof unbilledQuerySchema>

export interface BillingOverride {
  billable: boolean
  /** null: the computed bill amount. */
  billAmount: string | null
  reason: string
  setBy: ProjectUserRef | null
  setAt: string
}

export interface UnbilledLine {
  key: string
  sourceType: TmSourceType
  sourceId: string
  sourceLineId: string | null
  sourceNumber: string | null
  date: string
  description: string | null
  wbsNode: WbsNodeRef | null
  costCategory: CostCategoryRef | null
  party: ProjectFactParty | null
  /** Ex-VAT cost (negative for returns). Time (P6): the captured cost, null without projects.rates. */
  cost: string | null
  /** The company's markup at reading time, two decimals; null for time (billed at its rate). */
  markupPct: string | null
  /** What it bills: cost × (1 + markup ÷ 100); time: the captured bill amount; or the override. */
  billAmount: string
  /**
   * Time entries (P6): hours, the person, their rate role and the captured bill rate; null for costs.
   * Optional for older fixtures.
   */
  time?: {
    hours: string
    minutes: number
    person: ProjectUserRef
    rateRole: { id: string; code: string; name: string; nameAr: string | null } | null
    billRate: string | null
  } | null
  /** NO_BILL_RATE: time without a bill rate; it can't be invoiced until an override sets an amount. */
  blocked?: 'NO_BILL_RATE' | null
  billable: boolean
  override: BillingOverride | null
  openable: boolean
}

export interface UnbilledList {
  lines: UnbilledLine[]
  /** Σ billAmount of billable lines. */
  total: string
}

/** `PUT /projects/:id/unbilled/overrides` (projects.bill): write-offs and write-ups, each with a reason. */
export const billingOverridesSchema = z
  .object({
    overrides: z
      .array(
        z.object({
          key: tmSourceKeySchema,
          billable: z.boolean(),
          billAmount: moneySchema
            .refine((v) => Number(v) >= 0, 'Cannot be negative')
            .nullish()
            .transform((v) => v ?? null),
          reason: requiredText(500, 'Give a reason'),
        }),
      )
      .max(500)
      .default([]),
    /** Remove these overrides (back to the computed amount). */
    clear: z.array(tmSourceKeySchema).max(500).default([]),
  })
  .refine((v) => v.overrides.length > 0 || v.clear.length > 0, {
    path: ['overrides'],
    message: 'Change or clear at least one line',
  })
export type BillingOverridesInput = z.input<typeof billingOverridesSchema>

// ── preview and invoice ──────────────────────────────────────────────────────

const billingRequestFields = z.object({
  /** MILESTONE: these READY items. */
  itemIds: z.array(z.uuid()).max(200).default([]),
  /** TM: these billable source lines. */
  sourceKeys: z.array(tmSourceKeySchema).max(2000).default([]),
  groupBy: z.enum(TM_GROUPINGS).optional(),
  /** Defaults to today (Muscat). */
  invoiceDate: z.iso.date().optional(),
  /** Defaults to the invoice date + the customer's or tenant's payment terms. */
  dueDate: nullableDate,
  notes: optionalText(2000),
})
const oneKind = (v: { itemIds: string[]; sourceKeys: string[] }) =>
  v.itemIds.length > 0 !== v.sourceKeys.length > 0
const oneKindIssue = {
  path: ['itemIds'],
  message: 'Invoice either billing plan items or time-and-materials lines',
}

/** `POST /projects/:id/billing/preview` (projects.bill): the invoice that would be issued; writes nothing. */
export const billingPreviewSchema = billingRequestFields.refine(oneKind, oneKindIssue)
export type BillingPreviewInput = z.input<typeof billingPreviewSchema>

/**
 * `POST /projects/:id/billing/invoice` (projects.bill): issues and posts it. A retry with the same
 * `requestId` returns the first invoice (`created: false`); the same id for other items or sources is
 * refused (422 on `requestId`). Amounts are recomputed under the project lock.
 */
export const billingInvoiceSchema = billingRequestFields
  .extend({ requestId: z.uuid() })
  .refine(oneKind, oneKindIssue)
export type BillingInvoiceInput = z.input<typeof billingInvoiceSchema>

export interface BillingPreviewLine {
  description: string
  quantity: string
  unitPrice: string
  taxRate: TaxRateRefLite | null
  net: string
  tax: string
  total: string
}

export interface BillingPreview {
  kind: ProjectBillingKind
  customer: { id: string; code: string; name: string; nameAr: string | null }
  invoiceDate: string
  dueDate: string
  /** The invoice's reference: the project code (or the quotation number while it is the first document). */
  reference: string
  lines: BillingPreviewLine[]
  subtotal: string
  tax: string
  total: string
  /** Items or source lines billed. */
  billedCount: number
}

export interface BillingInvoiceResult {
  documentId: string
  invoiceId: string
  number: string
  total: string
  /** false: a retry returned the first invoice. */
  created: boolean
}

/**
 * `POST /projects/:id/billing-documents/:documentId/void` (projects.bill): voids the Sales document
 * with Sales' own rules (allocations, locked period), sets the link VOID and returns what it billed to
 * READY or unbilled.
 */
export const billingDocumentVoidSchema = z.object({ reason: requiredText(300, 'Give a reason') })
export type BillingDocumentVoidInput = z.input<typeof billingDocumentVoidSchema>

// ── starting a project from a quotation ──────────────────────────────────────

/**
 * `POST /projects/from-quotation/:quotationId` (projects.create + sales.read; 03 §7). The quotation must
 * be APPROVED and not converted (expired: `confirmExpired`); one project per quotation (409 IN_USE).
 * Copied: customer, contract value = the quotation's subtotal (ex-VAT), its tax mode and rate. With
 * FIXED_PRICE the selected lines become MANUAL billing items; with TIME_AND_MATERIALS a DRAFT budget
 * version 1 gets the subtotal as its revenue; PROGRESS_CLAIMS keeps the value (the BOQ arrives in P4).
 */
export const projectFromQuotationSchema = z.object({
  requestId: z.uuid(),
  typeId: nullableUuid,
  /** Default: "<customer> — <quotation number>". */
  name: optionalText(200),
  nameAr: optionalText(200),
  managerId: z.uuid().optional(),
  startDate: z.iso.date(),
  endDate: nullableDate,
  orgUnitId: z.uuid().nullable().optional(),
  /** Default: the type's. NON_BILLABLE is refused (a quotation is for a customer). */
  billingMethod: z.enum(['FIXED_PRICE', 'TIME_AND_MATERIALS', 'PROGRESS_CLAIMS']).optional(),
  /** The quotation lines that become billing items (FIXED_PRICE); default all. */
  lineIds: z.array(z.uuid()).max(500).optional(),
  applyTemplate: z.boolean().default(true),
  confirmExpired: z.boolean().default(false),
})
export type ProjectFromQuotationInput = z.input<typeof projectFromQuotationSchema>

// ── settings additions (group `projects`) ────────────────────────────────────

/** Markup on re-billed costs, percent with two decimals (ASM-199). */
export const DEFAULT_TM_EXPENSE_MARKUP_PCT = '0'
export const DEFAULT_TM_GROUP_BY: TmGrouping = 'ROLE'

// ── errors ───────────────────────────────────────────────────────────────────

export const PROJECT_BILLING_ERROR_CODES = [
  /** 409: Sales is off for the company: billing waits. */
  'SALES_MODULE_OFF',
  /** 422 (Sales): the document was issued by a project and is voided from it. */
  'PROJECT_DOCUMENT',
  /** 422 (Sales): the quotation started a project; bill it from the project. */
  'QUOTATION_STARTED_PROJECT',
  /** 422: the project is INTERNAL or not billed this way. */
  'PROJECT_NOT_BILLABLE',
] as const
export type ProjectBillingErrorCode = (typeof PROJECT_BILLING_ERROR_CODES)[number]
